import { useState } from "react";
import withRole from "../../Hooks/withRole";
import genericStyles from "../../Styles/genericStyles.module.css";
import { ETableEditAction } from "../../Utils/Enums";
import { BookModel } from "../../Utils/Models";
import { endpoints } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import GenericModal from "../GenericModal";
import BookForm from "./BookForm";
import { Box, Button, Typography } from "@mui/material";
import { FormsIds } from "../../Utils/Constants";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * BooksTable component renders a table of books with various columns
 * including name, work point, max per student, alert threshold, and price.
 * It utilizes the MUIDataTableCustomStyle component to display data and
 * provides functionalities for adding and editing book entries through a modal form.
 * State is managed for contextValue, form visibility, and table data reload triggers.
 */

function BooksTable(props: { restrictByWorkPoint?: boolean | undefined, delete?: boolean | undefined }) {
  const [contextValue, setContextValue] = useState(new BookModel());
  const [openForm, setOpenForm] = useState(false);
  const [triggerReload, setTriggerReload] = useState(false);
  const userId = props?.restrictByWorkPoint
    ? localStorage.getItem("userId")
    : undefined;

  /**
   * Columns definition for MUIDataTable component
   */
  const columns = [
    {
      name: "name",
      label: Vocabulary.name,
    },
    {
      name: "workPointId",
      label: Vocabulary.workPoint,
      options: {
        sortField: "workPoint.name",
        customBodyRender: (object: any) => {
          return object?.name;
        },
      },
    },
    {
      name: "maxPerStudent",
      label: Vocabulary.maxPerStudent,
    },
    {
      name: "alertThreshold",
      label: Vocabulary.alertThreshold,
    },
    {
      name: "price",
      label: Vocabulary.price,
    },
    {
      name: "quantity",
      label: Vocabulary.quantity,
      options: {
        customBodyRender: (value: number, tableMeta: any) => {
          const alertThresholdIndex = columns?.findIndex(
            (c) => c.name === "alertThreshold"
          );
          const alertThreshold = tableMeta.rowData[alertThresholdIndex];
          const showAlert = value < alertThreshold;

          return (
            <Box
              sx={{
                padding: 1,
                minWidth: 50,
                textAlign: "center",
                backgroundColor: showAlert
                  ? "rgba(255, 0, 0, 0.1)"
                  : "rgba(0, 128, 0, 0.1)",
                borderRadius: "10px",
                display: "flex",
                justifyContent: "center",
                gap: "1em",
              }}
            >
              {showAlert ? (
                <WarningAmberIcon sx={{ color: "red", fontSize: 28 }} />
              ) : (
                <CheckCircleIcon sx={{ color: "green", fontSize: 28 }} />
              )}
              <Typography sx={{ fontWeight: "bold", fontSize: 18 }}>
                {value}
              </Typography>
            </Box>
          );
        },
      },
    },
  ];

  /***
   * Render
   */
  return (
    <div className={genericStyles.mainContainer}>
      <MUIDataTableCustomStyle
        onEditAction={ETableEditAction.MODAL}
        title={Vocabulary.books}
        columns={columns}
        CRUDEndPoint={`${endpoints.books}/${userId}`}
        setContextValue={setContextValue}
        selectableRows={"none"}
        triggerReload={triggerReload}
        handleModalForm={setOpenForm}
        hideAddButton={userId !== undefined}
        hideEditButton={userId !== undefined}
        hideDeleteButton={props?.delete == false}
        addOrEditForm={
          <GenericModal
            title={Vocabulary.addBook}
            open={openForm}
            onClose={() => {
              setContextValue(new BookModel());
              setOpenForm(false);
            }}
            children={
              <BookForm
                onClose={() => {
                  setContextValue(new BookModel());
                  setOpenForm(false);
                  setTriggerReload(!triggerReload);
                }}
                selectedRow={contextValue}
              />
            }
            actions={
              <div className={genericStyles.editClientModalEditButton}>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => setOpenForm(false)}
                  startIcon={<CancelIcon />}
                >
                  {Vocabulary.cancel}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  form={FormsIds.bookForm}
                  color="success"
                  startIcon={<SaveIcon />}
                >
                  {Vocabulary.save}
                </Button>
              </div>
            }
          />
        }
      />
    </div>
  );
}

export default withRole(BooksTable);
